import fp from 'lodash/fp';
import PropTypes from 'prop-types';
import React, { useRef } from 'react';
import Autosuggest from 'react-autosuggest';
import ArrowPrimary from '../../Icons/Arrow-Primary.svg';
import ArrowDisabled from '../../Icons/Arrow-Disabled.svg';
import defaultStyles from '../common/dropdown.module.scss';
import TextInput from '../textinput/TextInput';
import { SuggestionsContainer, makeRenderSuggestion } from '../common/dropdownComponents';

const Dropdown = ({
    styles,
    options,
    getKeyValue,
    getDisplayValue,
    disabled,
    onSelection,
    value,
    textInputStyles,
    placeholder,
    maxSuggestions,
    height,
    portal,
    hideIcon
}) => {
    const inputRef = useRef(null);

    return (
        <Autosuggest
            suggestions={ options.length < maxSuggestions ? options : options.slice(0, maxSuggestions) }
            getSuggestionValue={ getDisplayValue }
            renderSuggestion={ makeRenderSuggestion(getDisplayValue, styles) }
            inputProps={ {
                onChange: fp.noop,
                value: value.toString(),
                readOnly: true,
                disabled
            } }
            onSuggestionSelected={ (event, { suggestion }) => onSelection(getKeyValue(suggestion)) }
            onSuggestionsFetchRequested={ fp.noop }
            onSuggestionsClearRequested={ fp.noop }
            renderSuggestionsContainer={ (props) => (
                <SuggestionsContainer
                    height={ height }
                    { ...props }
                    portal={ portal }
                    styles={ styles }
                    inputRef={ inputRef.current }
                />
            ) }
            shouldRenderSuggestions={ () => true }
            focusInputOnSuggestionClick={ false }
            renderInputComponent={ (inputProps) => {
                let arrowIcon;

                if (hideIcon) {
                    arrowIcon = null;
                } else if (inputProps.disabled) {
                    arrowIcon = ArrowDisabled;
                } else {
                    arrowIcon = ArrowPrimary;
                }

                return (
                    <>
                        <TextInput
                            styles={ textInputStyles }
                            icon={ arrowIcon }
                            inputProps={ {
                                ...inputProps,
                                onKeyDown: (e) => {
                                    if (e.key === 'Escape') {
                                        e.target.blur();
                                    }
                                }
                            } }
                            onChange={ fp.noop }
                            value={ inputProps.value }
                            placeholder={ placeholder }
                            disabled={ inputProps.disabled }
                        />
                        <div ref={ inputRef } className={ styles.dropdownAnchor } />
                    </>
                );
            } }
        />
    );
};

const valuePropType = PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object
]);

Dropdown.propTypes = {
    onSelection: PropTypes.func.isRequired,
    styles: PropTypes.objectOf(PropTypes.string),
    textInputStyles: PropTypes.shape({}),
    placeholder: PropTypes.string,
    getKeyValue: PropTypes.func,
    getDisplayValue: PropTypes.func,
    disabled: PropTypes.bool,
    options: PropTypes.arrayOf(valuePropType),
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    maxSuggestions: PropTypes.number,
    height: PropTypes.number,
    portal: PropTypes.bool,
    hideIcon: PropTypes.bool
};

Dropdown.defaultProps = {
    styles: defaultStyles,
    textInputStyles: undefined,
    placeholder: '',
    getKeyValue: fp.identity,
    getDisplayValue: fp.identity,
    disabled: false,
    options: [],
    value: '',
    maxSuggestions: Infinity,
    height: 200,
    portal: false,
    hideIcon: false
};

export default Dropdown;
